import { AuthProvider } from '@refinedev/core';
import axios, { isAxiosError } from 'axios';
import { env } from '@/config/env';
import { getAccessToken, setAccessToken, clearTokens } from '@/utils/storage';
import {
  decodeToken,
  getTokenRole,
  isTokenExpired,
  normalizeAdminJwtRole,
} from '@/utils/jwt';

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const response = await axios.post(
        `${env.API_URL}/auth/login`,
        {
          username: email,
          password: password,
        },
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      if (response.data?.access_token) {
        setAccessToken(response.data.access_token);
        return {
          success: true,
          redirectTo: '/',
        };
      }

      return {
        success: false,
        error: {
          name: 'Login Error',
          message: 'Invalid credentials',
        },
      };
    } catch (error: unknown) {
      const detail = isAxiosError(error)
        ? (error.response?.data as { detail?: string } | undefined)?.detail
        : undefined;
      return {
        success: false,
        error: {
          name: 'Login Error',
          message: detail ?? 'Login failed',
        },
      };
    }
  },

  logout: async () => {
    clearTokens();
    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    const token = getAccessToken();

    if (!token) {
      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
      };
    }

    if (isTokenExpired(token)) {
      clearTokens();
      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
      };
    }

    return {
      authenticated: true,
    };
  },

  getPermissions: async () => {
    const token = getAccessToken();
    if (!token) return null;

    return getTokenRole(token);
  },

  getIdentity: async () => {
    const token = getAccessToken();
    if (!token) return null;

    const decoded = decodeToken(token);
    if (!decoded) return null;

    const role = normalizeAdminJwtRole(decoded.role) ?? undefined;
    return {
      id: decoded.sub,
      name: decoded.username || decoded.email || 'User',
      email: decoded.email ?? undefined,
      role,
      avatar: undefined,
    };
  },

  onError: async (error) => {
    if (error.status === 401 || error.status === 403) {
      clearTokens();
      return {
        logout: true,
        redirectTo: '/login',
        error,
      };
    }

    return { error };
  },
};
